import posthog from 'posthog-js';
import { useLayoutEffect, useState } from 'react';
import { ValuesType } from 'utility-types';

import { milliseconds } from '~/interfaces';
import { backgroundTheme } from '~/modules/elements/layout-with-background';
import { ServerTransport } from '~/modules/remote-mic/network/server/transport/interface';
import { hasMicRelay } from '~/modules/remote-mic/network/server/transport/mic-relay-server';
import { FeatureFlags } from '~/modules/utils/feature-flags';
import Listener from '~/modules/utils/listener';
import storage from '~/modules/utils/storage';

export class Setting<T> extends Listener<[T]> {
  private value: T | undefined = undefined;

  public constructor(
    private name: string,
    private defaultValue: T,
    private driver: 'local' | 'session' | 'memory' = 'local',
  ) {
    super();
  }

  public get = (): T => {
    if (this.value === undefined) {
      this.value = storage[this.driver].getItem(`settings-${this.name}`)! ?? this.defaultValue;
    }
    return this.value as T;
  };

  public set = (newValue: T) => {
    this.value = newValue;

    storage[this.driver].setItem(`settings-${this.name}`, newValue);
    this.onUpdate(newValue);
  };
}

export const GraphicsLevel = ['high', 'low'] as const;
const GRAPHICS_LEVEL_KEY = 'graphics-level';
export const GraphicSetting = new Setting<ValuesType<typeof GraphicsLevel>>(GRAPHICS_LEVEL_KEY, GraphicsLevel[0]);

export const MicSetupPreference = [
  null,
  'skip',
  'remoteMics',
  'singstar-mics',
  'different-mics',
  'advanced',
  'built-in',
] as const;
const MIC_SETUP_PREFERENCE_KEY = 'mic-setup-preference';
export const MicSetupPreferenceSetting = new Setting<ValuesType<typeof MicSetupPreference>>(
  MIC_SETUP_PREFERENCE_KEY,
  MicSetupPreference[0],
  'session',
);

export const FpsCount = [60, 30] as const;
export const FPSCountSetting = new Setting<ValuesType<typeof FpsCount>>('fps-count', FpsCount[0]);

export const ExcludedLanguagesSetting = new Setting<string[] | null>('EXCLUDED_LANGUAGES_KEY_V2', null);

export const MobilePhoneModeSetting = new Setting<boolean | null>('MOBILE_PHONE_MODE_KEY', null);
export const KeyboardHelpVisibilitySetting = new Setting<boolean>('keyboard-help-visibility', true);

const initialInputLag = posthog.getFeatureFlagPayload?.(FeatureFlags.InitialInputLag);
export const InputLagSetting = new Setting<milliseconds>(
  'INPUT_LAG_V2',
  typeof initialInputLag === 'number' ? initialInputLag : 0,
);

// Used in remote mics
export const RemoteMicrophoneLagSetting = new Setting<milliseconds>('REMOTE_MIC_INPUT_LAG', 0, 'session');

export const RemoteMicPermissions = ['write', 'read'] as const;
export type RemoteMicPermission = ValuesType<typeof RemoteMicPermissions>;
export const DefaultRemoteMicPermission = new Setting<RemoteMicPermission>('DefaultRemoteMicPermission', 'write');

export const BackgroundThemeSetting = new Setting<backgroundTheme>('BackgroundThemeSetting', 'regular', 'memory');
export const AutoEnableFullscreenSetting = new Setting<boolean>(
  'AutoEnableFullscreenSetting',
  true,
  process.env.NODE_ENV === 'development' ? 'local' : 'session',
);

export const RemoteMicConnectionType = ['WebSockets', 'PartyKit', 'MicRelay', 'LanRelay'] as const;
/**
 * This project's own relay is preferred wherever one is configured - it is the only one that holds
 * a room open while the game reconnects. Builds without `VITE_APP_MIC_RELAY_URL` keep using the
 * shared PartyKit relay, so nothing changes until a deployment exists to point at.
 */
export const RemoteMicConnectionTypeSetting = new Setting<ServerTransport['name']>(
  'RemoteMicConnectionType',
  hasMicRelay() ? 'MicRelay' : 'PartyKit',
  'session',
);
/**
 * Origin that room links (remote-mic QR code, online lobby invite) point at. Empty means "use the
 * origin the game is served from", which is right on the web but unreachable from another device
 * in the desktop app (localhost). See `~/modules/utils/remote-mic-origin`.
 */
export const RemoteMicOriginSetting = new Setting<string>('REMOTE_MIC_ORIGIN', '');

/** Whether the desktop app's first-launch setup has been walked through (or dismissed). */
export const DesktopSetupCompletedSetting = new Setting<boolean>('DESKTOP_SETUP_COMPLETED', false);

/**
 * Volume, 0-100, adjusted by scrolling (see `VolumeScrollProvider`). Kept as three settings rather
 * than one so turning the music down does not also quieten the menu clicks, which are the only
 * feedback a player gets that a keypress landed.
 */
export const MasterVolumeSetting = new Setting<number>('MasterVolume', 100);
export const MusicVolumeSetting = new Setting<number>('MusicVolume', 100);
export const EffectsVolumeSetting = new Setting<number>('EffectsVolume', 100);

/**
 * Whether the difficulty row offers Debug 4/5/6 beside Easy/Medium/Hard. Off, so a dev build shows
 * the three real levels like every other build; it used to be gated on `isDev()`, which meant
 * `pnpm start` had no way to see what a player sees. Stored locally rather than per session because
 * nothing on screen turns it on - it is set from the console and should stay set.
 */
export const DebugDifficultiesSetting = new Setting<boolean>('DebugDifficulties', false);

export const IsCalibratedSetting = new Setting<boolean>('IsCalibrated', false);
export const UnassignOnSongFinishedSetting = new Setting<boolean>('UnassignOnSongFinished', false, 'session');

posthog.onFeatureFlags?.(() => {
  // A room hosted on this machine beats anything a flag can offer: it is the only one with no
  // internet in the path at all. Flags still steer everyone else.
  if (RemoteMicConnectionTypeSetting.get() === 'LanRelay') return;

  RemoteMicConnectionTypeSetting.set(
    (posthog.getFeatureFlagPayload(FeatureFlags.RemoteMicConnectionType) as ServerTransport['name']) ??
      (hasMicRelay() ? 'MicRelay' : 'PartyKit'),
  );
});

export function useSettingValue<T>(value: Setting<T>) {
  const [currentValue, setCurrentValue] = useState(() => value.get());

  useLayoutEffect(() => {
    return value.addListener(setCurrentValue);
  }, [value]);

  return [currentValue, value.set] as const;
}
